import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Chip,
  Avatar,
  Divider,
  Stack,
  LinearProgress,
  Button,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as CalendarIcon,
  AssignmentTurnedIn as InterventionIcon,
  EmojiEvents as AwardIcon,
  LocalHospital as NurseIcon,
  School as TuitionIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Student } from '@/types';
import { AttendanceHistory } from './AttendanceHistory';
import { AttendanceStats } from './AttendanceStats';
import { ContactForm } from './ContactForm';
import { ContactList } from './ContactList';
import { Contact } from '../types';

interface StudentDetailsModalProps {
  student: Student | null;
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`student-tabpanel-${index}`}
    aria-labelledby={`student-tab-${index}`}
    sx={{ py: 3 }}
  >
    {value === index && children}
  </Box>
);

// Mock contacts data
const mockContacts: Contact[] = [
  {
    id: '1',
    firstName: 'John',
    lastName: 'Smith',
    relationship: 'parent_guardian',
    priority: 'primary',
    phone: '(555) 123-4567',
    email: 'john.smith@email.com',
    isPrimary: true
  },
  {
    id: '2',
    firstName: 'Mary',
    lastName: 'Johnson',
    relationship: 'emergency_contact',
    priority: 'emergency',
    phone: '(555) 987-6543',
    email: 'mary.j@email.com',
    isPrimary: false
  }
];

export const StudentDetailsModal: React.FC<StudentDetailsModalProps> = ({
  student,
  open,
  onClose
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>(mockContacts);
  const [selectedContact, setSelectedContact] = useState<Contact | undefined>();

  if (!student) return null;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getRiskLevel = (rate: number) => {
    if (rate < 85) return { label: 'Critical', color: 'error' };
    if (rate < 90) return { label: 'At Risk', color: 'warning' };
    return { label: 'Good', color: 'success' };
  };

  const risk = getRiskLevel(student.attendanceRate);

  const handleSaveContact = (contactData: Partial<Contact>) => {
    if (selectedContact) {
      // Update existing contact
      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id ? { ...contact, ...contactData } : contact
      ));
    } else {
      // Add new contact
      const newContact: Contact = {
        ...contactData,
        id: `contact-${Date.now()}`,
        isPrimary: contacts.length === 0
      } as Contact;
      setContacts(prev => [...prev, newContact]);
    }
    setShowContactForm(false);
    setSelectedContact(undefined);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setShowContactForm(true);
  };

  const handleCancelEdit = () => {
    setShowContactForm(false);
    setSelectedContact(undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="student-details-title"
    >
      <DialogTitle id="student-details-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={student.photo}
              sx={{ width: 56, height: 56 }}
            />
            <Box>
              <Typography variant="h6">
                {student.firstName} {student.lastName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={`Grade ${student.grade}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<WarningIcon />}
                  label={risk.label}
                  color={risk.color as any}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Quick Stats */}
        <Box sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Attendance Rate
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="h4">
                    {student.attendanceRate}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={student.attendanceRate}
                  color={risk.color as any}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  GPA
                </Typography>
                <Typography variant="h4">
                  {student.academicPerformance.gpa.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {student.academicPerformance.coursesFailing} courses at risk
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Days Absent
                </Typography>
                <Typography variant="h4">
                  {student.daysAbsent}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last attended: {format(new Date(student.lastAttendanceDate || ''), 'MMM d, yyyy')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Interventions
                </Typography>
                <Typography variant="h4">
                  {student.interventions.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active interventions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Overview" id="student-tab-0" />
            <Tab label="Attendance" id="student-tab-1" />
            <Tab label="Academic" id="student-tab-2" />
            <Tab label="Contact" id="student-tab-3" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ px: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Recent Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Student has shown declining attendance patterns over the past month.
                    Multiple attempts have been made to contact guardians.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Action Items
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<CalendarIcon />}
                      size="small"
                    >
                      Schedule Parent Conference
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<InterventionIcon />}
                      size="small"
                    >
                      Create Intervention Plan
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AwardIcon />}
                      size="small"
                    >
                      Award PBIS Points
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<NurseIcon />}
                      size="small"
                    >
                      Create Nurse Referral
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<TuitionIcon />}
                      size="small"
                    >
                      Create Tuition Referral
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <AttendanceStats student={student} />
            <Box sx={{ mt: 3 }}>
              <AttendanceHistory student={student} />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Current Schedule
                </Typography>
                {student.schedule?.map((period, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      Period {period.period}: {period.className}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {period.teacherName} • Room {period.classroomNumber}
                    </Typography>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Academic Performance
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Current GPA: {student.academicPerformance.gpa.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credits Earned: {student.academicPerformance.credits}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Courses at Risk: {student.academicPerformance.coursesFailing}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  Contacts
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedContact(undefined);
                    setShowContactForm(true);
                  }}
                >
                  Add Contact
                </Button>
              </Stack>
            </Box>

            {showContactForm ? (
              <Paper sx={{ p: 3, mb: 3 }}>
                <ContactForm
                  contact={selectedContact}
                  onSubmit={handleSaveContact}
                  onCancel={handleCancelEdit}
                />
              </Paper>
            ) : (
              <ContactList
                contacts={contacts}
                onEdit={handleEditContact}
              />
            )}
          </TabPanel>
        </Box>
      </DialogContent>
    </Dialog>
  );
};